/**
 * Users — Departments tab (W1a-08)
 *
 * Tenant-scoped department CRUD. Each department has:
 *   - Name + description
 *   - Optional parent department (forms a tree)
 *   - Optional department head (a user in the same tenant)
 *
 * v1 of W1a-08 lands the table + CRUD without the user-to-department
 * association (`users.department_id` lands as F-W1a-08.2 paired with
 * W1a-04's Edit User dialog adding a Department picker). The
 * `memberCount` is rendered as 0 for now; the cards still show the
 * member-list section but with an "Assignment lands in F-W1a-08.2"
 * empty state.
 *
 * The mock `DEPARTMENTS` constant in `mock-users-data.ts` is preserved
 * because other consumers (Create User dialog, etc.) still reference
 * it — those will swap to the hook in their own features (F-W1a-08.3).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  Plus, Search, Building2, Users as UsersIcon, Crown, MoreHorizontal,
  Pencil, Trash2, Copy, AlertCircle, Loader2, Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
  useUsers,
  type DepartmentRow,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';
import { UserAvatar } from '@/components/users/user-badges';

export const Route = createFileRoute('/users/departments')({
  component: DepartmentsTab,
});

function DepartmentsTab() {
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<DepartmentRow | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const searchTerm = search.trim();
  const { departments, assignedMemberCount, pagination, isLoading } = useDepartments({
    search: searchTerm || undefined,
    // Review BF-B2bT3-03 — this screen has no pagination control, so a page it
    // does not fetch is a department nobody here can open, rename or delete.
    // The API's maximum (users.ts: `pageSize … .max(200)`) is the most one
    // request can carry; when even that is not everything the header says so
    // rather than letting the missing rows pass as absent.
    pageSize: 200,
  });
  const deleteMutation = useDeleteDepartment();

  const canWrite = useHasPermission('write', 'users');
  // W1a-08 Decision D1: department delete uses write:users (not delete:users)
  // so admins can manage org structure without owner escalation.
  const canDelete = canWrite;

  const openCreate = () => {
    setEditingDept(null);
    setEditorOpen(true);
  };

  const openEdit = (d: DepartmentRow) => {
    setEditingDept(d);
    setEditorOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ departmentId: id });
      toast.success('Department deleted');
      setConfirmDeleteId(null);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to delete department';
      // Surface the server error code if present in the message.
      if (msg.includes('DEPARTMENT_HAS_CHILDREN')) {
        toast.error('Cannot delete: department has children', {
          description: 'Reassign or delete child departments first.',
        });
      } else if (msg.includes('DEPARTMENT_IN_USE')) {
        toast.error('Cannot delete: department has members', {
          description: 'Reassign members to another department first.',
        });
      } else {
        toast.error(msg);
      }
    }
  };

  // B2b-T3 review BF-B2bT3-03 — THE DEPARTMENT COUNT IS THE SERVER'S, NOT THE
  // PAGE'S. It was `departments.length`, the length of the current RESPONSE
  // PAGE: this tab asks for no `pageSize`, so the API's default of 50 applies
  // and there is no pagination control on this screen — a tenant with 60
  // departments read "50 departments" and the other ten were neither counted
  // nor reachable. `pagination.totalCount` is the number of departments the
  // query matched; `departments.length` is how many of them this page carries,
  // and the two are said separately below rather than blurred into one figure.
  const totalCount = pagination?.totalCount ?? departments.length;
  const shownCount = departments.length;
  // B2b-T3 — THE TENANT TOTAL COMES FROM THE SERVER, and this is the B1c
  // lesson in its smallest form. It was `departments.reduce((sum, d) => sum +
  // d.memberCount, 0)`, which was right for exactly as long as a person could
  // be in one department: this batch lets an admin put somebody in two, and
  // that sum then counts them twice in a figure labelled "total members" —
  // with every API check passing, because the error only exists on this line.
  // It also only ever saw the current PAGE. `assignedMemberCount` counts each
  // person once across the whole tenant, from the same rows the per-department
  // counts read.
  const totalMembers = assignedMemberCount;
  const deptToDelete =
    confirmDeleteId
      ? departments.find((d) => d.id === confirmDeleteId) ?? null
      : null;

  return (
    <div className="flex flex-col h-full p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Departments</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {/* Review BF-B2bT3-03 — a SEARCH-SCOPED count and a TENANT-WIDE one
                cannot share a sentence unlabelled. Typing "Operations" used to
                render "1 department, 240 people assigned" directly above a card
                reading "12 members", and an operator reads 240 as the
                Operations headcount. The matching count says what it matched;
                the tenant figure says it is the tenant's. */}
            Organizational structure.{' '}
            {searchTerm
              ? `${shownCount} of ${totalCount} ${totalCount === 1 ? 'department' : 'departments'} match “${searchTerm}”`
              : `${totalCount} ${totalCount === 1 ? 'department' : 'departments'}`}
            {/* "assigned to a department", not a headcount: somebody with no
                membership row is an ALL-DEPARTMENTS person (decisions.md C7),
                not a person in no department, and they are not in this
                figure. `null` means the list has not loaded — the screen says
                nothing rather than inventing a zero. */}
            {totalMembers !== null && totalMembers > 0
              ? `, ${totalMembers} ${totalMembers === 1 ? 'person' : 'people'} assigned across the tenant`
              : ''}.
            {!isLoading && shownCount < totalCount && (
              <span className="text-amber-600 dark:text-amber-400">
                {' '}Showing the first {shownCount}; narrow the search to reach the rest.
              </span>
            )}
          </p>
        </div>
        {canWrite && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Department
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="relative max-w-md">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search departments…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-xs">Loading departments…</span>
        </div>
      ) : departments.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <Building2 className="h-8 w-8 text-muted-foreground mb-3" />
            {/* Closes #133 — distinguish filter-empty from data-empty.
                The hook server-filters by search, so departments.length
                being 0 ambiguously meant "tenant has none" or "search
                excluded all". Branch on whether search is active. */}
            {search.trim() !== '' ? (
              <>
                <p className="text-sm font-medium">No departments match your search</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  Try a different search term or clear it to see all departments.
                </p>
                <Button size="sm" variant="outline" className="mt-4" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">No departments yet</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  {canWrite
                    ? 'Create your first department to start organizing users by team or function.'
                    : 'No departments are configured for this tenant.'}
                </p>
                {canWrite && (
                  <Button size="sm" className="mt-4" onClick={openCreate}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Create department
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {departments.map((d) => (
            <DepartmentCard
              key={d.id}
              department={d}
              canWrite={canWrite}
              canDelete={canDelete}
              onEdit={() => openEdit(d)}
              onDelete={() => setConfirmDeleteId(d.id)}
            />
          ))}
        </div>
      )}

      <DepartmentEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        department={editingDept}
        existingDepartments={departments}
      />

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete department?</AlertDialogTitle>
            <AlertDialogDescription>
              {deptToDelete
                ? `Delete "${deptToDelete.name}"? This cannot be undone. The server will refuse the delete if the department has child departments.`
                : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmDeleteId && handleDelete(confirmDeleteId)
              }
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete department'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface DepartmentCardProps {
  department: DepartmentRow;
  canWrite: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function DepartmentCard({
  department,
  canWrite,
  canDelete,
  onEdit,
  onDelete,
}: DepartmentCardProps) {
  return (
    <Card className="flex flex-col">
      <CardContent className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" title={department.name}>
                {department.name}
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <UsersIcon className="h-2.5 w-2.5" />
                {department.memberCount}{' '}
                {department.memberCount === 1 ? 'member' : 'members'}
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Department actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canWrite && (
                <DropdownMenuItem className="text-xs" onSelect={onEdit}>
                  <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-xs"
                onSelect={() => {
                  navigator.clipboard.writeText(department.id);
                  toast.success('Department ID copied');
                }}
              >
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy ID
              </DropdownMenuItem>
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-red-600 dark:text-red-400"
                    disabled={department.memberCount > 0}
                    onSelect={onDelete}
                    title={
                      department.memberCount > 0
                        ? 'Reassign all members to another department before deleting'
                        : 'Delete department'
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete department
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {department.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {department.description}
          </p>
        )}

        {department.parentName && (
          <div className="text-[11px] text-muted-foreground mb-2">
            Parent: <span className="font-medium">{department.parentName}</span>
          </div>
        )}

        {department.headUserName && (
          <div className="flex items-center gap-2 rounded-md bg-muted/30 p-2 mb-3">
            <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <UserAvatar name={department.headUserName} size="xs" />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-medium truncate">
                {department.headUserName}
              </div>
              <div className="text-[10px] text-muted-foreground truncate">
                Department Head
              </div>
            </div>
          </div>
        )}

        <div className="flex-1" />

        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
            Members
          </p>
          <p className="text-[11px] text-muted-foreground tabular-nums">
            {department.memberCount === 0
              ? 'No members yet — assign in Edit User.'
              : `${department.memberCount} member${department.memberCount === 1 ? '' : 's'}`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Editor dialog ──────────────────────────────────────────────────────────

interface DepartmentEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  department: DepartmentRow | null;
  existingDepartments: DepartmentRow[];
}

function DepartmentEditorDialog({
  open,
  onOpenChange,
  department,
  existingDepartments,
}: DepartmentEditorDialogProps) {
  const isEdit = department !== null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string>('none');
  const [headUserId, setHeadUserId] = useState<string>('none');
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();
  const { users } = useUsers({ pageSize: 200 });
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!open) return;
    setServerError(null);
    if (department) {
      setName(department.name);
      setDescription(department.description ?? '');
      setParentId(department.parentId ?? 'none');
      setHeadUserId(department.headUserId ?? 'none');
    } else {
      setName('');
      setDescription('');
      setParentId('none');
      setHeadUserId('none');
    }
  }, [open, department]);

  const canSave = name.trim().length >= 1 && name.trim().length <= 200;
  const parentOptions = existingDepartments.filter(
    (d) => d.id !== department?.id,
  );
  const userOptions = users.filter((u) => u.status === 'active');

  const handleSave = async () => {
    if (!canSave) return;
    setServerError(null);

    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    const body = {
      name: trimmedName,
      description: trimmedDesc.length > 0 ? trimmedDesc : null,
      parentId: parentId === 'none' ? null : parentId,
      headUserId: headUserId === 'none' ? null : headUserId,
    };

    try {
      if (isEdit && department) {
        await updateMutation.mutateAsync({
          departmentId: department.id,
          body,
        });
        toast.success(`Department "${trimmedName}" updated`);
      } else {
        await createMutation.mutateAsync(body);
        toast.success(`Department "${trimmedName}" created`);
      }
      onOpenChange(false);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to save department';
      // Map common server codes to user-friendly inline errors.
      if (msg.includes('NAME_CONFLICT')) {
        setServerError(
          `A department with name "${trimmedName}" already exists in this tenant.`,
        );
      } else if (msg.includes('INVALID_PARENT')) {
        setServerError(
          'The selected parent department is invalid (cycle or not in this tenant).',
        );
      } else if (msg.includes('INVALID_HEAD_USER')) {
        setServerError(
          'The selected head user is not a member of this tenant.',
        );
      } else {
        setServerError(msg);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            {isEdit ? 'Edit department' : 'New department'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the department name, description, parent, and head.'
              : 'Departments group users by organizational structure within your tenant.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Engineering"
                className="h-9 text-xs"
                maxLength={200}
              />
              <p className="text-[10px] text-muted-foreground">
                Must be unique within your tenant (case-insensitive).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this department do?"
                rows={2}
                className="text-xs"
                maxLength={1000}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Parent department</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs">
                    — Top-level —
                  </SelectItem>
                  {parentOptions.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-xs">
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                Optional. Used for org-chart grouping.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <Crown className="h-3 w-3 text-amber-500" />
                Department head
              </Label>
              <Select value={headUserId} onValueChange={setHeadUserId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none" className="text-xs">
                    — No head —
                  </SelectItem>
                  {userOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-xs">
                      {u.fullName} — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {serverError && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div className="text-xs text-red-800 dark:text-red-300">
                  {serverError}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-end gap-2 w-full">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : isEdit ? (
                <>
                  <Save className="h-4 w-4 mr-1.5" /> Save changes
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1.5" /> Create department
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
